import type {MolecularGraphJSON} from "./types";
import type {GraphAtom} from "./GraphAtom";
import type {GraphBond} from "./GraphBond";
import {detectRings} from "./ringDetection";

export class MolecularGraph {
    readonly atoms: GraphAtom[];
    readonly bonds: GraphBond[];
    private adjacency: Map<GraphAtom, GraphBond[]>;

    constructor(json: MolecularGraphJSON) {
        this.atoms = json.a.map((aj, i) => ({
            index: i,
            symbol: aj.s,
            atomicNumber: aj.n,
            charge: aj.q ?? 0,
            lonePairs: aj.lp ?? 0,
            implicitH: aj.h ?? 0,
            radicals: aj.r ?? 0,
            ...(aj.c && {chirality: aj.c}),
            pos: aj.p,
            visible: aj.v ?? true,
        }));

        this.bonds = json.b.map(bj => {
            const a1 = this.atoms[bj.a];
            const a2 = this.atoms[bj.b];
            return {
                a1,
                a2,
                order: bj.o,
                type: bj.t,
                ...(bj.i && {isomerConfig: bj.i}),
                getOtherAtom: (a: GraphAtom) => (a === a1 ? a2 : a1),
            };
        });

        this.adjacency = new Map();
        for (const atom of this.atoms) {
            this.adjacency.set(atom, []);
        }
        for (const bond of this.bonds) {
            this.adjacency.get(bond.a1)!.push(bond);
            this.adjacency.get(bond.a2)!.push(bond);
        }
    }

    getBondsAttachedTo(atom: GraphAtom): GraphBond[] {
        return this.adjacency.get(atom) ?? [];
    }

    /** Connected components — each is a MolecularGraph over a subset of atoms/bonds. */
    getMolecules(): MolecularGraph[] {
        const visited = new Set<GraphAtom>();
        const components: {atoms: GraphAtom[]; bonds: GraphBond[]}[] = [];

        for (const atom of this.atoms) {
            if (visited.has(atom)) continue;
            const compAtoms: GraphAtom[] = [];
            const queue = [atom];
            while (queue.length > 0) {
                const curr = queue.shift()!;
                if (visited.has(curr)) continue;
                visited.add(curr);
                compAtoms.push(curr);
                for (const bond of this.getBondsAttachedTo(curr)) {
                    const other = bond.getOtherAtom(curr);
                    if (!visited.has(other)) queue.push(other);
                }
            }
            const compBonds = this.bonds.filter(
                b => compAtoms.includes(b.a1) && compAtoms.includes(b.a2)
            );
            components.push({atoms: compAtoms, bonds: compBonds});
        }

        return components.map(comp => MolecularGraph.fromSubset(comp.atoms, comp.bonds));
    }

    getRings(): GraphAtom[][] {
        return detectRings(this.atoms, (a) => this.getBondsAttachedTo(a));
    }

    /** Count of implicit hydrogens on an atom (from JSON `h` field). */
    getImplicitHCount(atom: GraphAtom): number {
        return atom.implicitH;
    }

    /** Total H count = explicit H bonds + implicit H count. */
    getTotalHCount(atom: GraphAtom): number {
        const explicitH = this.getBondsAttachedTo(atom)
            .filter(b => b.getOtherAtom(atom).symbol === "H" && b.order === 1)
            .length;
        return explicitH + atom.implicitH;
    }

    /** Build a MolecularGraph from a pre-existing subset of atoms and bonds.
     *  Re-indexes atoms starting from 0 while preserving object identity. */
    static fromSubset(atoms: GraphAtom[], bonds: GraphBond[]): MolecularGraph {
        const graph = Object.create(MolecularGraph.prototype) as MolecularGraph;

        (graph as any).atoms = atoms;
        (graph as any).bonds = bonds;

        const adjacency = new Map<GraphAtom, GraphBond[]>();
        for (const atom of atoms) adjacency.set(atom, []);
        for (const bond of bonds) {
            adjacency.get(bond.a1)?.push(bond);
            adjacency.get(bond.a2)?.push(bond);
        }
        (graph as any).adjacency = adjacency;

        return graph;
    }
}
